import { useState } from 'react';

/**
 * Custom hook for managing snackbar notifications
 *
 * @returns {Object} Snackbar utilities and component
 * @returns {Function} showSuccess - Show success message
 * @returns {Function} showError - Show error message
 * @returns {Function} showWarning - Show warning message
 * @returns {Function} showInfo - Show info message
 * @returns {Function} SnackbarComponent - Function that returns the snackbar component
 *
 * @example
 * const { showSuccess, showError, SnackbarComponent } = useSnackbar();
 *
 * // Show success message
 * showSuccess('Operation completed successfully');
 *
 * // Show error message
 * showError('Something went wrong');
 *
 * // Render in component
 * return (
 *   <>
 *     <YourContent />
 *     <SnackbarComponent />
 *   </>
 * );
 */
export const useSnackbar = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const showSuccess = (message) => showSnackbar(message, 'success');
  const showError = (message) => showSnackbar(message, 'error');
  const showWarning = (message) => showSnackbar(message, 'warning');
  const showInfo = (message) => showSnackbar(message, 'info');

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    snackbar,
    handleClose
  };
};

export default useSnackbar;
